import type { Copy } from "../types";

export const uk: Copy = {
    seo: {
      title: "Калькулятор віку авто | перевірка за роком і місяцем",
      description:
        "Перевірте точний вік автомобіля за роком виробництва та необов’язковим місяцем. Дивіться роки, місяці, дні, загальну кількість днів і етапи в одному чистому інструменті.",
      keywords: [
        "калькулятор віку авто",
        "вік автомобіля",
        "вік авто за роком",
        "рік виробництва авто",
        "місяць виробництва авто",
        "скільки років моєму авто",
        "перевірка віку авто",
        "калькулятор віку RC",
        "калькулятор дати авто",
        "калькулятор віку авто за місяцем",
        "альтернатива mParivahan",
        "альтернатива Parivahan",
        "калькулятор віку авто RTO",
        "перевірка віку реєстрації авто",
        "вік реєстрації авто",
      ],
      faq: [
        {
          question: "Що робить цей калькулятор?",
          answer:
            "Він обчислює точний вік автомобіля за вибраним роком виробництва та необов’язковим місяцем, а потім показує роки, місяці, дні та інформацію про етапи.",
        },
        {
          question: "Навіщо потрібен необов’язковий місяць?",
          answer:
            "Додавання місяця робить розрахунок точнішим. Якщо місяць не вибрано, застосунок використовує січень обраного року.",
        },
        {
          question: "Чи можу я вибрати майбутній рік або місяць?",
          answer:
            "Ні. Майбутні роки заблоковані, а майбутні місяці вимикаються, якщо вибраний рік є поточним.",
        },
      ],
    },
    headerTagline: "Помічник для авто",
    headerTitle: "Калькулятор віку авто",
    reset: "Скинути",
    switchToLight: "Світлий режим",
    switchToDark: "Темний режим",
    languageLabel: "Мова",
    languageEnglish: "Англійська",
    languageHindi: "Гінді",
    heroEyebrow: "Вік авто, чітко",
    heroTitle: "Чистий інструмент для віку авто.",
    heroDescription:
      "Оберіть рік виробництва. Додавайте місяць лише якщо хочете точніший результат.",
    yearSelectLabel: "Рік виробництва",
    monthSelectLabel: "Місяць виробництва",
    includeMonthLabel: "Додати конкретний місяць для більшої точності",
    includeMonthNote: "Якщо місяць не додавати, для розрахунку використовується січень.",
    yearOnlyBadge: "Лише рік",
    yearMonthBadge: "Рік + місяць",
    yearPlaceholder: "Оберіть рік",
    monthPlaceholder: "Оберіть місяць",
    inputTitle: "Рік виробництва",
    inputLabel: "Рік виробництва",
    blockedDateNote: "Майбутні роки заблоковані.",
    result: "Результат",
    vehicleAgeLead: "Ваш автомобіль",
    asOfToday: "Станом на сьогодні",
    years: "Роки",
    months: "Місяці",
    days: "Дні",
    totalDays: (count) => `${count.toLocaleString("en-IN")} днів загалом`,
    ageLabels: { almostNew: "Майже нове", modern: "Сучасне", mature: "Зріле", classic: "Класичне" },
    estimatedValue: {
      high: "Високий потенціал перепродажу",
      healthy: "Здорове збереження вартості",
      moderate: "Помірна амортизація",
      collector: "Може зрости інтерес колекціонерів",
    },
    maintenanceTitle: "Обслуговування",
    maintenance: {
      routine: "Регулярне обслуговування та OEM-догляд",
      watch: "Стежте за гальмами, рідинами та шинами",
      plan: "Плануйте зношувані деталі та огляди",
      specialized: "Спеціалізоване обслуговування стає важливим",
    },
    registrationTitle: "Реєстрація",
    registration: {
      collector: "У деяких регіонах може застосовуватися статус колекційного авто",
      classic: "У деяких регіонах може застосовуватися класична реєстрація",
      standard: "Очікуйте стандартні поновлення та регіональні перевірки",
      regular: "Зазвичай діє звичайна реєстрація",
    },
    upcomingMilestoneTitle: "Найближчий етап",
    milestoneTimelineTitle: "Хронологія етапів",
    milestoneRemaining: ({ label, days }) => `${label} через ${days.toLocaleString("uk-UA")} дн.`,
    reached: "Досягнуто",
    upcoming: "Найближчий",
    allMilestonesReachedTitle: "Усі перелічені етапи досягнуто",
    allMilestonesReachedBody:
      "Цей автомобіль уже вийшов за межі діапазону етапів, який зараз показано в застосунку.",
    copySummary: "Скопіювати підсумок",
    shareResult: "Поділитися результатом",
    resetMessage: "Калькулятор скинуто",
    summaryCopied: "Підсумок скопійовано в буфер обміну",
    clipboardFailed: "Не вдалося отримати доступ до буфера обміну",
    emptyTitle: "Оберіть рік виробництва, щоб побачити повний розбір",
    emptyDescription:
      "Після вибору року ви побачите точний вік, загальні дні, примітки щодо обслуговування, класичний статус і час етапів.",
    shareTitle: "Калькулятор віку авто",
    summary: ({ label, years, months, days, asOf }) =>
      `Мій автомобіль має дату виробництва ${label}, і станом на ${asOf} йому ${years} років, ${months} місяців і ${days} днів.`,
    milestones: {
      1: { label: "Перший рік", note: "Корисно для гарантії та планування сервісу." },
      3: { label: "Три роки", note: "Поширений етап для перепродажу та зміни вартості." },
      5: { label: "П’ять років", note: "Гарний час для глибшого бюджету на обслуговування." },
      10: { label: "Десять років", note: "Старіші компоненти потребують більшої уваги." },
      15: { label: "П’ятнадцять років", note: "У багатьох регіонах тут починається класичне ставлення." },
      25: { label: "Двадцять п’ять років", note: "Може бути цікавим для колекціонерів і імпорту." },
    },
  };
